import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';

import { useIsMobile } from '@/global-hooks/use-is-mobile';
import type {
  PipelineBoard as PipelineBoardDto,
  PipelineBoardSort,
  PipelineDragMove,
} from '@/pages/pipelines/common/types';

import { PipelineCard } from './card';
import { PipelineBoardColumns } from './columns';
import { usePipelineBoardColumns } from './hooks/use-pipeline-board-columns';
import { usePipelineCardDrag } from './hooks/use-pipeline-card-drag';
import { usePipelineOptimisticBoard } from './hooks/use-pipeline-optimistic-board';
import { PipelineStageTabs } from './stage-tabs';
import { BoardShell, CardSlot } from './styled';

type Props = {
  board: PipelineBoardDto;
  onSelectCard: (_cardId: string) => void;
  onMoveCard?: (_move: PipelineDragMove) => Promise<void> | void;
  // Capability flag: lets a consumer without drag support (e.g. a fund-scoped
  // board) render the same columns/cards without wiring dnd-kit at all.
  // Defaults to true so existing callers (investment/deals) are unaffected.
  dragEnabled?: boolean;
  allExpanded?: boolean;
  filteredStages?: string[];
  sort?: PipelineBoardSort;
  activeOnly?: boolean;
  movePending?: boolean;
  onToggleStageFilter?: (_stage: string) => void;
  onToggleSort?: () => void;
  onAddCard?: (_stage: string) => void;
};

export const PipelineBoard = ({
  board,
  onSelectCard,
  onMoveCard,
  dragEnabled = true,
  allExpanded = false,
  filteredStages = [],
  sort,
  activeOnly = false,
  movePending = false,
  onToggleStageFilter,
  onToggleSort,
  onAddCard,
}: Props) => {
  const isMobile = useIsMobile();
  const dragAvailable = !isMobile && dragEnabled && Boolean(onMoveCard);
  const { optimisticBoard, addOptimisticMove } = usePipelineOptimisticBoard(board);

  const { columns, renderedColumns, expandedStage, setExpandedStage } = usePipelineBoardColumns({
    board: optimisticBoard,
    sort,
    activeOnly,
    isMobile,
  });

  const { activeCard, sensors, dragSourceStage, onDragStart, onDragCancel, onDragEnd } =
    usePipelineCardDrag({
      columns,
      expandedStage,
      setExpandedStage,
      onMoveCard,
      addOptimisticMove,
    });

  const boardColumns = (
    <PipelineBoardColumns
      columns={renderedColumns}
      expandedStage={expandedStage}
      allExpanded={allExpanded && !isMobile}
      isMobile={isMobile}
      filteredStages={filteredStages}
      sort={sort}
      dragAvailable={dragAvailable}
      dragInteractionDisabled={movePending}
      dragSourceStage={dragSourceStage}
      onExpand={setExpandedStage}
      onSelectCard={onSelectCard}
      onToggleStageFilter={onToggleStageFilter}
      onToggleSort={onToggleSort}
      onAddCard={onAddCard}
    />
  );

  return (
    <BoardShell>
      <PipelineStageTabs
        columns={columns}
        expandedStage={expandedStage}
        onSelectStage={setExpandedStage}
      />

      {dragAvailable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
          onDragEnd={onDragEnd}
        >
          {boardColumns}
          <DragOverlay dropAnimation={null}>
            {activeCard ? (
              <CardSlot data-testid="pipeline-card-drag-clone">
                <PipelineCard card={activeCard} isDragging />
              </CardSlot>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        boardColumns
      )}
    </BoardShell>
  );
};

export * from './states';
